import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Keyboard, Mouse, Clock, CheckSquare, MessageSquare, Search, Plus, ArrowUpDown, Command } from 'lucide-react';

const sections = [
  {
    title: 'Task Navigation',
    icon: ArrowUpDown,
    color: 'text-[#5B6FD6]',
    bg: 'bg-[#5B6FD6]/10',
    shortcuts: [
      { keys: ['↑'], desc: 'Move to previous task' },
      { keys: ['↓'], desc: 'Move to next task' },
      { keys: ['Enter'], desc: 'Open selected task' },
      { keys: ['Esc'], desc: 'Deselect / close dialog' },
    ],
  },
  {
    title: 'Task Actions',
    icon: CheckSquare,
    color: 'text-[#10B981]',
    bg: 'bg-[#10B981]/10',
    shortcuts: [
      { keys: ['T'], desc: 'Start / pause timer on selected task' },
      { keys: ['S'], desc: 'Open status dropdown for selected task' },
      { keys: ['C'], desc: 'Quick comment on selected task' },
      { keys: ['A'], desc: 'Select / deselect all tasks' },
    ],
  },
  {
    title: 'Creation & Search',
    icon: Plus,
    color: 'text-amber-600',
    bg: 'bg-amber-50',
    shortcuts: [
      { keys: ['N'], desc: 'Create new task (Admin / Super Admin)' },
      { keys: ['/'], desc: 'Focus search bar' },
    ],
  },
  {
    title: 'Help',
    icon: Command,
    color: 'text-slate-600',
    bg: 'bg-slate-100',
    shortcuts: [
      { keys: ['?'], desc: 'Toggle shortcuts help dialog (Tasks page)' },
    ],
  },
];

const tips = [
  { icon: Mouse, text: 'Hover over a task row to reveal the manual time entry (pencil) icon' },
  { icon: Clock, text: 'Only one timer can run at a time — starting a new one pauses the active one' },
  { icon: Search, text: 'Use the filter bar on the Tasks page to quickly narrow by status, client, or assignee' },
  { icon: MessageSquare, text: 'Quick comments can be added inline without opening the task detail page' },
];

export default function Shortcuts() {
  return (
    <div className="p-8 min-h-full bg-slate-50" data-testid="shortcuts-page">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-slate-900 mb-1 flex items-center gap-3" data-testid="shortcuts-heading">
          <Keyboard className="h-8 w-8 text-[#5B6FD6]" /> Keyboard Shortcuts
        </h1>
        <p className="text-slate-500">Quick reference for all keyboard shortcuts and productivity tips</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-6">
        {sections.map(section => (
          <Card key={section.title} className="border-slate-200 shadow-sm" data-testid={`section-${section.title.toLowerCase().replace(/ /g, '-')}`}>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <div className={`h-8 w-8 rounded-lg ${section.bg} flex items-center justify-center`}>
                  <section.icon className={`h-4 w-4 ${section.color}`} />
                </div>
                {section.title}
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="space-y-0">
                {section.shortcuts.map(s => (
                  <div key={s.desc} className="flex items-center justify-between py-2.5 border-b border-slate-100 last:border-0">
                    <span className="text-sm text-slate-700">{s.desc}</span>
                    <div className="flex items-center gap-1">
                      {s.keys.map(k => (
                        <kbd key={k} className="px-2.5 py-1 bg-slate-100 border border-slate-200 rounded-md text-xs font-mono font-semibold text-slate-700 min-w-[32px] text-center shadow-sm">
                          {k}
                        </kbd>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card className="border-slate-200 shadow-sm" data-testid="tips-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Badge className="bg-[#5B6FD6]/10 text-[#5B6FD6] border-[#5B6FD6]/20">Tips</Badge>
            Productivity Tips
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {tips.map((tip) => (
              <div key={tip.text} className="flex items-start gap-3 p-3 rounded-lg bg-slate-50 border border-slate-100">
                <tip.icon className="h-4 w-4 text-[#5B6FD6] flex-shrink-0 mt-0.5" />
                <span className="text-sm text-slate-600">{tip.text}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
